import { hasOwnProp } from './objectUtils';
import { stripPrefix, upperCase } from './stringUtils';
import { isFunction, isObject, isString, strictIsFinite } from './typeChecks';
import { COMMA_WITH_OPTIONAL_SPACE, EMAIL_REGEX, EVENT_HANDLER_REGEX, FIRST_LETTER, ONLY_WHITESPACE } from './constants';

var createChangeHandler = function createChangeHandler() {
  var {
    demaskFn,
    generateMeta,
    hasMaxLen = true,
    preprocessFn,
    props = {},
    validationFn
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return e => {
    e === null || e === void 0 ? void 0 : e.persist();
    var {
      changeHandler,
      value: oldValue
    } = props;

    if (isFunction(changeHandler)) {
      var {
        value: rawValue,
        maxLength
      } = e.target;
      var preprocessedValue = isFunction(preprocessFn) ? preprocessFn(rawValue) : rawValue;
      var shouldDemask = isFunction(demaskFn);
      var newValue = shouldDemask ? demaskFn(preprocessedValue) : preprocessedValue;
      var metaBase = {};

      if (hasMaxLen) {
        metaBase.isMaxLength = maxLength === preprocessedValue.length;
      }

      var validationMeta = {
        props
      };

      if (['checkbox', 'radio'].includes(e.target.type)) {
        validationMeta.isChecked = e.target.checked;
      } else {
        validationMeta.maskedValue = shouldDemask ? preprocessedValue : null;
      }

      var validation = isFunction(validationFn) ? validationFn(newValue, validationMeta) : {};
      var generatedMeta = isFunction(generateMeta) ? generateMeta(e, newValue, oldValue) : {};
      var metadata = Object.assign(metaBase, {
        validation
      }, generatedMeta);
      changeHandler(e, newValue, oldValue, metadata);
    }
  };
};

var filterProps = function filterProps() {
  var props = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var filterKeys = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];

  if (!isObject(props)) {
    return {};
  }

  if (isString(filterKeys)) {
    filterKeys = filterKeys.split(COMMA_WITH_OPTIONAL_SPACE);
  }

  if (!Array.isArray(filterKeys)) {
    return props;
  }

  return Object.entries(props).reduce((filtered, _ref) => {
    var [key, value] = _ref;

    if (!filterKeys.includes(key)) {
      filtered[key] = value;
    }

    return filtered;
  }, {});
};

var genericValidationFn = (value, _ref2) => {
  var {
    maskedValue,
    props
  } = _ref2;
  var {
    max,
    maxLength,
    min,
    minLength,
    pattern = '',
    required = false,
    step,
    type
  } = props;
  var isPresent = Boolean(value.length) && !ONLY_WHITESPACE.test(value);
  var isMaskedInput = maskedValue !== null;
  var valueToCompareToAttrs = isMaskedInput ? maskedValue : value;
  var valueMissing = required && !isPresent;
  var patternMismatch = false;
  var rangeOverflow = false;
  var rangeUnderflow = false;
  var stepMismatch = false;
  var tooLong = false;
  var tooShort = false;
  var typeMismatch = false;

  if (isPresent) {
    var valueAsNum = +value;
    var valueIsFinite = strictIsFinite(valueAsNum);
    var maxAsNum = +max;

    if (valueIsFinite && strictIsFinite(maxAsNum)) {
      rangeOverflow = maxAsNum < valueAsNum;
    }

    var minAsNum = +min;

    if (valueIsFinite && strictIsFinite(minAsNum)) {
      rangeUnderflow = minAsNum > valueAsNum;
    }

    var patternString = (pattern === null || pattern === void 0 ? void 0 : pattern.pattern) || pattern;

    if (isString(patternString) && patternString.length) {
      var {
        flags = ''
      } = pattern;
      var patternRegEx = new RegExp("^".concat(patternString, "$"), flags);
      patternMismatch = !patternRegEx.test(valueToCompareToAttrs);
    }

    if (hasOwnProp(props, 'step') && step !== 'any') {
      stepMismatch = valueAsNum % +step !== 0;
    }

    if (isString(maxLength) || strictIsFinite(maxLength)) {
      tooLong = valueToCompareToAttrs.length > +maxLength;
    }

    if (isString(minLength) || strictIsFinite(minLength)) {
      tooShort = valueToCompareToAttrs.length < +minLength;
    }

    if (type === 'email') {
      typeMismatch = !EMAIL_REGEX.test(value);
    }
  }

  var result = {
    patternMismatch,
    rangeOverflow,
    rangeUnderflow,
    stepMismatch,
    tooLong,
    tooShort,
    typeMismatch,
    valueMissing
  };
  result.errors = Object.keys(result).filter(key => result[key]);
  result.isValid = !result.errors.length;
  return result;
};

var mapEventHandler = handler => {
  if (!EVENT_HANDLER_REGEX.test(handler)) {
    return handler;
  }

  var eventName = handler.replace(EVENT_HANDLER_REGEX, '').replace(FIRST_LETTER, upperCase);
  return "on".concat(eventName);
};

var parsePatternProp = pattern => {
  if (!isString(pattern) && !isString(pattern === null || pattern === void 0 ? void 0 : pattern.pattern)) {
    return;
  }

  return (pattern === null || pattern === void 0 ? void 0 : pattern.pattern) || pattern;
};

var splitPropsByPrefix = function splitPropsByPrefix(props, prefixes) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var {
    filter,
    mapEventHandlers = false
  } = options;

  if (!isObject(props)) {
    return {};
  }

  if (filter) {
    props = filterProps(props, filter);
  }

  if (isString(prefixes)) {
    prefixes = prefixes.split(COMMA_WITH_OPTIONAL_SPACE);
  }

  if (!Array.isArray(prefixes) || !prefixes.length) {
    return props;
  }

  var filterRegEx = new RegExp("^".concat(prefixes.join('|^')));
  return Object.entries(props).reduce((returnObj, _ref3) => {
    var [key, value] = _ref3;
    var [prefix] = key.match(filterRegEx) || [];
    var group = prefix || '__rest__';
    var prop = prefix ? stripPrefix(key, filterRegEx) : key;

    if (!hasOwnProp(returnObj, group)) {
      returnObj[group] = {};
    }

    if (mapEventHandlers) {
      prop = mapEventHandler(prop);
    }

    returnObj[group][prop] = value;
    return returnObj;
  }, {});
};

export { createChangeHandler, filterProps, genericValidationFn, mapEventHandler, parsePatternProp, splitPropsByPrefix };